import { addCSRFHeader, requiresCSRF } from '../utils/csrf';

const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:8000';
console.log('[SimpleLabs] API URL configured:', API_BASE_URL);

export interface APIResponse<T = any> {
  status: 'success' | 'error' | 'partial_success' | 'all_duplicates';
  data?: T;
  error?: string;
  message?: string;
}

/**
 * Centralized API service for making authenticated requests using session cookies
 * Simplified to use session-based authentication instead of JWT tokens
 */
class APIService {
  /**
   * Make an authenticated request using session cookies
   */
  async request<T = any>(url: string, options: RequestInit = {}): Promise<APIResponse<T>> {
    let headers: Record<string, string> = {};
    
    // Don't set Content-Type for FormData - let browser set it
    if (!(options.body instanceof FormData)) {
      headers['Content-Type'] = 'application/json';
    }

    // Add CSRF token for state-changing requests
    if (requiresCSRF(options.method || 'GET')) {
      headers = await addCSRFHeader(headers);
    }

    // Add timeout using AbortController
    const controller = new AbortController();
    const timeout = setTimeout(() => controller.abort(), 10000); // 10 second timeout

    try {
      const response = await fetch(`${API_BASE_URL}${url}`, {
        ...options,
        headers: {
          ...headers,
          ...options.headers,
        },
        credentials: 'include', // CRITICAL: Include session cookies in requests
        signal: controller.signal,
      });
      
      clearTimeout(timeout);

      // Handle non-JSON responses (like HTML error pages)
      const contentType = response.headers.get('content-type');
      let data;
      
      if (contentType && contentType.includes('application/json')) {
        data = await response.json();
      } else {
        // If not JSON, read as text and create error
        const text = await response.text();
        console.error('Non-JSON response:', text.substring(0, 200));
        data = { 
          error: `Server error: ${response.status} ${response.statusText}`,
          status: 'error' 
        };
      }

      if (!response.ok) {
        // For session-based auth, 401 errors should be handled by redirecting to login
        // No token refresh logic needed since sessions are managed server-side
        const error = new Error(data.message || data.error || 'Request failed');
        (error as any).status = response.status;
        throw error;
      }

      return data;
    } catch (error: any) {
      clearTimeout(timeout);
      
      if (error.name === 'AbortError') {
        console.error(`Request timeout for ${url}`);
        const timeoutError = new Error('Request timed out');
        (timeoutError as any).status = 408;
        throw timeoutError;
      }
      
      throw error;
    }
  }


  // Convenience methods for common HTTP verbs
  async get<T = any>(url: string): Promise<APIResponse<T>> {
    return this.request<T>(url, { method: 'GET' });
  }

  async post<T = any>(url: string, data?: any): Promise<APIResponse<T>> {
    return this.request<T>(url, {
      method: 'POST',
      body: data ? JSON.stringify(data) : undefined,
    });
  }

  async put<T = any>(url: string, data?: any): Promise<APIResponse<T>> {
    return this.request<T>(url, {
      method: 'PUT',
      body: data ? JSON.stringify(data) : undefined,
    });
  }

  async delete<T = any>(url: string): Promise<APIResponse<T>> {
    return this.request<T>(url, { method: 'DELETE' });
  }

  // Reports-specific methods
  async getReports() {
    return this.get('/api/reports');
  }

  async getReport(reportId: number) {
    return this.get(`/api/reports/${reportId}`);
  }

  async updateReport(reportId: number, updates: any) {
    return this.put(`/api/reports/${reportId}`, updates);
  }

  async deleteReport(reportId: number) {
    return this.delete(`/api/reports/${reportId}`);
  }

  async getUniqueTests() {
    return this.get('/api/tests/unique');
  }

  async getTrends(params: URLSearchParams, abortSignal?: AbortSignal) {
    return this.request(`/api/trends?${params.toString()}`, { 
      method: 'GET',
      signal: abortSignal 
    });
  }

  /**
   * Get trends with retry logic and better error handling
   */
  async getTrendsWithRetry(
    params: URLSearchParams, 
    options?: {
      maxRetries?: number;
      retryDelay?: number;
      abortSignal?: AbortSignal;
      onPartialData?: (data: any) => void;
    }
  ): Promise<APIResponse<any>> {
    const { maxRetries = 3, retryDelay = 1000, abortSignal, onPartialData } = options || {};
    let lastError: Error | null = null;

    for (let attempt = 0; attempt < maxRetries; attempt++) {
      try {
        // Add retry attempt to params for backend tracking
        const retryParams = new URLSearchParams(params);
        if (attempt > 0) {
          retryParams.set('retry', attempt.toString());
        }

        const response = await this.getTrends(retryParams, abortSignal);
        
        // Handle partial success
        if (response.status === 'partial_success' && onPartialData) {
          onPartialData(response.data);
        }
        
        return response;
      } catch (error: any) {
        lastError = error;
        
        // Don't retry on client cancellation
        if (error.name === 'AbortError') {
          throw error;
        }
        
        // Don't retry on 4xx errors (except 408 timeout)
        if (error.status && error.status >= 400 && error.status < 500 && error.status !== 408) {
          throw error;
        }
        
        // Wait before retrying (exponential backoff)
        if (attempt < maxRetries - 1) {
          await new Promise(resolve => setTimeout(resolve, retryDelay * Math.pow(2, attempt)));
        }
      }
    }

    // All retries failed
    throw lastError || new Error('Failed to fetch trends after retries');
  }

  async updateLabResult(resultId: number, updates: any) {
    return this.put(`/api/results/${resultId}`, updates);
  }

  async saveReportsBatch(reports: any[]) {
    return this.post('/api/reports/batch', { reports });
  }

  async extractBatch(formData: FormData) {
    return this.request('/api/extract-batch', {
      method: 'POST',
      body: formData,
      headers: {}, // Don't set Content-Type for FormData - let browser set it
    });
  }

  async previewCSV(formData: FormData) {
    return this.request('/api/import/csv/preview', {
      method: 'POST',
      body: formData,
      headers: {}, // Don't set Content-Type for FormData - let browser set it
    });
  }

  async processCSV(data: any) {
    return this.post('/api/import/csv/process', data);
  }

  async checkDuplicate(data: any) {
    return this.post('/api/check-duplicate', data);
  }
}

// Export a singleton instance
export const apiService = new APIService();
export default apiService;